import { useState } from 'react';

import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useCreatePortal } from 'api/portals/hooks';
import { CreatePortalData } from 'api/portals/requests';

export const useCreatePortalModal = () => useModal(CreatePortal);

const Form = createForm<CreatePortalData>();

const getErrorMessage = (error: any, fallback: string): string => {
  if (error?.response?.data?.name) {
    return error.response.data.name;
  }
  if (error?.message) {
    return error.message;
  }

  return fallback;
};

export const CreatePortal = () => {
  const { t } = useScopedTranslate(locale);
  const createPortalMutation = useCreatePortal();
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const [error, setError] = useState<string | undefined>(undefined);

  const handleSubmit = (data: CreatePortalData) => {
    if (!data.name?.trim()) {
      return;
    }
    setError(undefined);

    createPortalMutation.mutate(data, {
      onSuccess: () => hideModal(),
      onError: (error: unknown) => {
        const errorMessage = getErrorMessage(error, t('genericError'));
        setError(errorMessage);
      },
    });
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={t('createPortal')} />
      <Modal.Content>
        <Form store={formStore} onSubmit={handleSubmit}>
          <Stack space={6}>
            <Stack space={2}>
              <Form.TextField
                id="name"
                label={t('portalName')}
                placeholder={t('placeholder')}
                onChange={() => setError(undefined)}
              />
              <FormError text={error} />
            </Stack>
            <Stack space={2} orientation="horizontal" align="center" justify="flex-end">
              <Button variant="secondary" text={t('core.cancel')} onClick={hideModal} />
              <Form.Submit
                variant="primary-blue"
                text={t('createPortal')}
                isDisabled={!isDirty || createPortalMutation.isLoading || Boolean(error)}
              />
            </Stack>
          </Stack>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

export const locale = {
  en: {
    createPortal: 'Create new portal',
    portalName: 'Portal name',
    placeholder: 'Name your new portal...',
    genericError: 'An error occurred while creating the portal. Please try again.',
  },
};
